import * as React from 'react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { es, ptBR } from 'react-day-picker/locale';
import { useSelector } from 'react-redux';
import { cn } from './utils';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  locale: localeProp,
  formatters,
  components,
  ...props
}) {
  const language = useSelector((state) => state.reducerLanguage);
  const locale = localeProp ?? (language?.data === 'es' ? es : ptBR);
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      captionLayout={captionLayout}
      navLayout="around"
      className={cn(
        'group/calendar w-fit bg-white p-3 [--cell-size:2rem]',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code || 'pt-BR', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
        month: cn(
          'flex w-full flex-col gap-4 group-data-[nav-layout=around]/calendar:grid group-data-[nav-layout=around]/calendar:grid-cols-[--cell-size_1fr_--cell-size] group-data-[nav-layout=around]/calendar:items-center group-data-[nav-layout=around]/calendar:gap-y-4',
          defaultClassNames.month
        ),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          'inline-flex items-center justify-center rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 h-[--cell-size] w-[--cell-size] select-none p-0',
          'group-data-[nav-layout=around]/calendar:col-start-1 group-data-[nav-layout=around]/calendar:row-start-1',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          'inline-flex items-center justify-center rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 h-[--cell-size] w-[--cell-size] select-none p-0',
          'group-data-[nav-layout=around]/calendar:col-start-3 group-data-[nav-layout=around]/calendar:row-start-1',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]',
          'group-data-[nav-layout=around]/calendar:col-start-2 group-data-[nav-layout=around]/calendar:row-start-1 group-data-[nav-layout=around]/calendar:px-0',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          'relative rounded-md border border-gray-200 shadow-sm focus-within:ring-2 focus-within:ring-gray-200',
          defaultClassNames.dropdown_root
        ),
        dropdown: cn('absolute inset-0 opacity-0 bg-white', defaultClassNames.dropdown),
        caption_label: cn(
          'select-none font-medium flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-3.5 [&>svg]:text-gray-400',
          defaultClassNames.caption_label
        ),
        month_grid: cn(
          'w-full border-collapse group-data-[nav-layout=around]/calendar:col-span-3 group-data-[nav-layout=around]/calendar:row-start-2',
          defaultClassNames.month_grid
        ),
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-gray-500 flex-1 select-none rounded-md text-[0.8rem] font-normal',
          defaultClassNames.weekday
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        day: cn(
          'group/day relative aspect-square h-full w-full select-none p-0 text-center',
          defaultClassNames.day
        ),
        today: cn('bg-gray-100 text-gray-900 rounded-md', defaultClassNames.today),
        outside: cn('text-gray-400', defaultClassNames.outside),
        disabled: cn('text-gray-400 opacity-50', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className: rootClassName, rootRef, ...rootProps }) => (
          <div
            data-slot="calendar"
            ref={rootRef}
            className={cn(rootClassName)}
            {...rootProps}
          />
        ),
        Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon className={cn('size-4', chevronClassName)} {...chevronProps} />
            );
          }
          if (orientation === 'right') {
            return (
              <ChevronRightIcon className={cn('size-4', chevronClassName)} {...chevronProps} />
            );
          }
          return (
            <ChevronDownIcon className={cn('size-4', chevronClassName)} {...chevronProps} />
          );
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({ className, day, modifiers, ...props }) {
  const defaultClassNames = getDefaultClassNames();
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString('pt-BR')}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      className={cn(
        'flex aspect-square h-auto w-full min-w-[--cell-size] items-center justify-center rounded-md text-sm font-normal leading-none text-gray-900',
        'hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
        'data-[selected-single=true]:bg-gray-900 data-[selected-single=true]:text-white',
        'disabled:pointer-events-none disabled:opacity-50',
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
